import { useState } from "react";
import { Heart, Trash2, X, FolderPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PHOTO } from "@/constants/testIds";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function SelectionBar({ selectedIds, onClear, onChanged }) {
  const [albumOpen, setAlbumOpen] = useState(false);
  const [album, setAlbum] = useState("");
  const count = selectedIds.length;

  const bulk = async (action, value) => {
    try {
      await api.post("/photos/bulk", { ids: selectedIds, action, value });
      toast.success(`${count} photo${count === 1 ? "" : "s"} updated`);
      onChanged?.();
      onClear();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (count === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#2D232E] text-white rounded-full pl-5 pr-2 py-2 flex items-center gap-2 shadow-[0_10px_40px_rgba(0,0,0,0.25)] animate-in fade-in slide-in-from-bottom-4">
        <span className="font-hand text-xl">{count} selected</span>
        <button
          data-testid={PHOTO.bulkFav}
          onClick={() => bulk("favorite")}
          className="ml-1 h-9 px-3 rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-transform hover:scale-105"
        >
          <Heart className="w-4 h-4" /> Favorite
        </button>
        <button
          data-testid={PHOTO.bulkAlbum}
          onClick={() => setAlbumOpen(true)}
          className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold inline-flex items-center gap-1.5"
        >
          <FolderPlus className="w-4 h-4" /> Album
        </button>
        <button
          data-testid={PHOTO.bulkDel}
          onClick={() => { if (confirm(`Delete ${count} photos?`)) bulk("delete"); }}
          className="h-9 px-3 rounded-full bg-white/10 hover:bg-red-500/70 text-white text-sm font-semibold inline-flex items-center gap-1.5"
        >
          <Trash2 className="w-4 h-4" /> Delete
        </button>
        <button
          onClick={onClear}
          className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          aria-label="Clear selection"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <Dialog open={albumOpen} onOpenChange={setAlbumOpen}>
        <DialogContent className="sm:max-w-sm rounded-3xl border-[#EAE2D6] bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#2D232E]">Move to album</DialogTitle>
          </DialogHeader>
          <Input
            value={album}
            onChange={(e) => setAlbum(e.target.value)}
            placeholder="Album name (leave blank to remove)"
            className="rounded-xl h-11 bg-white border-[#EAE2D6]"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAlbumOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={async () => { await bulk("assign_album", album); setAlbumOpen(false); setAlbum(""); }}
              className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold px-6"
            >
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
